import {
  EventListInterface,
  EventListItemInterface,
  IdNameEntryInterface,
  IdNameEnumEntryInterface,
} from '@elearning-platform/api-types/interfaces';
import {
  Breakpoint,
  Calendar,
  Color,
  ContentFilter,
  ContentFilterOption,
  ContentFilters,
  ContentFilterType,
  EmptyPlaceholder,
  EventCardGroup,
  EventTileCompactData,
  EventTileDataType,
  EventTileDetailedData,
  EventTileLayout,
  getPathWithParams,
  ShapeVariant,
  Typography,
  TypographyVariant,
  useFormatDate,
  UseFormatDate,
} from '@elearning-platform/ui';
import { UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import React, { MutableRefObject, useCallback, useMemo, useRef } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { getEventList } from '../../api/requests';
import { EventFormDrawer } from '../../components';
import { QueryKey, RouteNameEnum, ViewImage } from '../../enums';
import { useEventDisenroll, useEventEnrollmentSubmit, useEventFormDrawerData, useRedirectTo } from '../../hooks';
import { UseEventEnrollmentSubmit, UseEventFormDrawerData, UseRedirectTo } from '../../interfaces';
import { getUrlByRouteName } from '../../utils';

import { FilterName } from './enums';
import { useFilteredData, useFilters } from './hooks';
import Thumbnail from './images/defaultPlaceholder.jpg';
import ShapeBg from './images/shapeBg.png';
import { EventGroup, UseFilters } from './interfaces';
import {
  BgImage,
  BgImageContainer,
  CalendarSection,
  Container,
  EventsGroupContainer,
  EventsSection,
  FiltersContainer,
  FiltersShape,
  FiltersShapeContainer,
  Title,
} from './trainingsV2.styled';

const TrainingsV2: React.FC = (): JSX.Element => {
  const isTablet: boolean = useMediaQuery({
    query: Breakpoint.Tablet,
  });
  const { t }: TransProps<never> = useTranslation();
  const { data, isFetching, isPending }: UseQueryResult<EventListInterface> = useQuery({
    queryKey: [QueryKey.Events],
    queryFn: (): Promise<EventListInterface> => getEventList(),
  });
  const { getTimestampMilisecValue }: UseFormatDate = useFormatDate();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const allowedCategories: string[] | undefined = useMemo((): string[] | undefined => data?.filters.categories.map(
    (category: IdNameEnumEntryInterface): string => category.enum), [data]);
  const allowedTypes: string[] | undefined = useMemo((): string[] | undefined => data?.filters.tags.map(
    (tag: IdNameEntryInterface): string => tag.id.toString()), [data]);
  const {
    filters,
    setFilters,
    setSearchValue,
    appliedSearchValue,
    searchValue,
  }: UseFilters = useFilters(allowedCategories ?? [], allowedTypes ?? []);
  const filteredEvents: EventListItemInterface[] = useFilteredData(data?.elements ?? [], filters, appliedSearchValue);
  const initialSearchRef: MutableRefObject<string> = useRef<string>(searchValue);
  const trainingUrl: string | undefined = getUrlByRouteName(RouteNameEnum.TrainingDetails)?.url;

  const {
    mutate: disenroll,
    isPending: isDisenrollPending,
    variables: disenrollVariables,
  }: UseMutationResult<void, Error, number> = useEventDisenroll();
  const { drawerSettings, eventData, survey, isFormLoading }: UseEventFormDrawerData = useEventFormDrawerData();
  const { handleEnrollSubmit, isSubmitting }: UseEventEnrollmentSubmit = useEventEnrollmentSubmit({
    id: eventData.id,
    attemptId: eventData.attemptId?.toString(),
    enrollSurvey: survey?.enrollSurvey,
  });

  const mapEventToTileData: (element: EventListItemInterface, layout: EventTileLayout, onCardClick?: () => void) => EventTileDetailedData
  = useCallback((element: EventListItemInterface, layout: EventTileLayout, onCardClick?: () => void): EventTileDetailedData => ({
    type: EventTileDataType.Detailed,
    layout,
    img: element.course.image ?? ViewImage.TrainingsDefaultThumbnail,
    name: element.course.name,
    id: element.id,
    date: {
      from: getTimestampMilisecValue(element.date.from),
      to: getTimestampMilisecValue(element.date.to),
    },
    isAttending: element.userFlags?.isEnrolled,
    eventType: element.course.category?.enum === 'exams' ? 'exam' : 'event',
    location: element.location?.city,
    seats: {
      taken: element.availableEnrolments,
      total: element.totalEnrolments,
    },
    ...(onCardClick ? { onCardClick } : undefined),
  }), [getTimestampMilisecValue]);

  const calendarEvents: EventTileDetailedData[] = useMemo(
    (): EventTileDetailedData[] => (
      filteredEvents?.map((element: EventListItemInterface): EventTileDetailedData => mapEventToTileData(
        element,
        EventTileLayout.Vertical,
        (): void => redirect({ route: RouteNameEnum.TrainingDetails, params: { id: element.id.toString() } })
      )) ?? []
    ),
    [filteredEvents, redirect, mapEventToTileData]
  );

  const transformGroupToEventCard: (element: EventListItemInterface) => EventTileCompactData = useCallback(
    (element: EventListItemInterface): EventTileCompactData => ({
      id: element.id,
      type: EventTileDataType.Compact,
      date: {
        from: getTimestampMilisecValue(element.date.from),
        to: getTimestampMilisecValue(element.date.to),
      },
      eventType: element.course.category?.enum === 'exams' ? 'exam' : 'event',
      location: element.location?.city,
      seats: {
        taken: element.availableEnrolments,
        total: element.totalEnrolments,
      },
      isAttending: element.userFlags?.isEnrolled,
      actions: {
        primaryButtonData: {
          label: element.userFlags?.isEnrolled
            ? t('competence.app.trainingsV2.disenroll')
            : t('competence.app.trainingsV2.enroll'),
          isLoading: !!(isDisenrollPending && disenrollVariables === element.id),
          onClick: (): void => element.userFlags?.isEnrolled
            ? disenroll(element.id)
            : drawerSettings.openDrawer(element.id.toString()),
        },
        secondaryButtonData: (element.userFlags?.isEnrolled && element.entrySurvey?.attemptId)
          ? { onClick: (): void => drawerSettings.openDrawer(element.id.toString()) }
          : undefined,
        detailsLinkData: { internalPath: getPathWithParams(trainingUrl ?? '', { id: element.id.toString() }) },
      },
    }),
    [t, disenroll, isDisenrollPending, disenrollVariables, getTimestampMilisecValue, trainingUrl, drawerSettings],
  );

  const events: EventGroup[] = useMemo<EventGroup[]>((): EventGroup[] => {
    const helperMap: Map<number, EventGroup> = new Map<number, EventGroup>();

    return (filteredEvents ?? []).reduce<EventGroup[]>(
      (acc: EventGroup[], event: EventListItemInterface): EventGroup[] => {
        const existingGroup: EventGroup | undefined = helperMap.get(event.course.id);

        if (existingGroup) {
          existingGroup.events.push(event);
        } else {
          const newGroup: EventGroup = { course: event.course, events: [event] };
          acc.push(newGroup);
          helperMap.set(event.course.id, newGroup);
        }

        return acc;
      },
      []
    );
  }, [filteredEvents]);

  const mapCategoryFilterToOption: (categoryFilter: IdNameEnumEntryInterface) => ContentFilterOption
  = (categoryFilter: IdNameEnumEntryInterface): ContentFilterOption => ({
    value: categoryFilter.enum,
    label: categoryFilter.name,
  });

  const mapTypeFilterToOption: (typeFilter: IdNameEntryInterface) => ContentFilterOption
  = (typeFilter: IdNameEntryInterface): ContentFilterOption => ({
    value: typeFilter.id.toString(),
    label: typeFilter.name,
  });

  const contentFilters: ContentFilter[] = useMemo((): ContentFilter[] => [
    {
      name: FilterName.Category,
      label: t('competence.app.trainingsV2.category'),
      type: ContentFilterType.Multiselect,
      options: data?.filters.categories.map(mapCategoryFilterToOption) ?? [],
    },
    {
      name: FilterName.Type,
      label: t('competence.app.trainingsV2.type'),
      type: ContentFilterType.Multiselect,
      options: data?.filters.tags.map(mapTypeFilterToOption) ?? [],
    },
    {
      name: FilterName.DatesRange,
      type: ContentFilterType.DatesRange,
      datesRangeLabelFrom: t('competence.app.trainingsV2.dateFrom'),
      datesRangeLabelTo: t('competence.app.trainingsV2.dateTo'),
      label: t('competence.app.trainingsV2.date'),
    },
  ], [data, t]);

  const selectedEvent: EventListItemInterface | undefined = useMemo(
    (): EventListItemInterface | undefined => eventData.id
      ? data?.elements.find((element: EventListItemInterface): boolean => element.id.toString() === eventData.id)
      : undefined,
    [data, eventData.id]
  );

  return (
    <Container>
      <EventFormDrawer
        eventData={selectedEvent
          ? mapEventToTileData(selectedEvent, EventTileLayout.Horizontal)
          : undefined
        }
        formSettings={{
          isFormLoading: isFormLoading || eventData.isLoading,
          isSubmitting,
          initialData: survey?.defaultValues,
          onSubmit: handleEnrollSubmit,
          addonsConfig: {
            withAccommodation: !!selectedEvent?.entrySurvey?.withAccommodation,
            withParking: !!selectedEvent?.entrySurvey?.withParking,
            withDinner: !!selectedEvent?.entrySurvey?.withDinner,
            withVege: !!selectedEvent?.entrySurvey?.withVege,
          },
        }}
        isOpen={drawerSettings.isOpen}
        onClose={drawerSettings.closeDrawer}
      />
      <FiltersContainer>
        <ContentFilters
          filters={contentFilters}
          initialSearchValue={initialSearchRef.current}
          initialSelectedFilters={filters}
          onFiltersChange={setFilters}
          onSearchValueChange={setSearchValue}
          searchPlaceholder={t('competence.app.trainingsV2.searchEvents')}
        >
          {!!isTablet && (
            <FiltersShapeContainer>
              <FiltersShape color={Color.Brand300} variant={ShapeVariant.DD} />
              <FiltersShape color={Color.Brand300} variant={ShapeVariant.DD} />
            </FiltersShapeContainer>
          )}
        </ContentFilters>
      </FiltersContainer>
      <CalendarSection>
        <Calendar events={calendarEvents} />
      </CalendarSection>
      <EventsSection>
        <Typography variant={isTablet ? TypographyVariant.DisplaySM : TypographyVariant.DisplayXS}>
          <Title text={t('competence.app.trainingsV2.title')} />
        </Typography>
        <EventsGroupContainer>
          {(isFetching || isPending)
            ? <EmptyPlaceholder isLoading text={t('competence.app.trainingsV2.eventsLoading')} />
            : (
                events.length
                  ? events.map((group: EventGroup): JSX.Element => (
                    <EventCardGroup
                      coverImg={group.course.image ?? Thumbnail}
                      eventType={group.course.category?.enum === 'exams' ? 'exam' : 'event'}
                      events={group.events.map(transformGroupToEventCard)}
                      header={group.course.name}
                      key={group.course.id}
                    />
                  ))
                  : <EmptyPlaceholder text={t('competence.app.trainingsV2.noEventsFound')} />
              )
          }
        </EventsGroupContainer>
      </EventsSection>
      <BgImageContainer>
        <BgImage src={ShapeBg} />
      </BgImageContainer>
    </Container>
  );
};

export default TrainingsV2;
